/**
 * fetchSubitemsBoard(monday, itemId) → { board, itemName }
 *
 * The ONE way to reach an item's subitems board, shared by the Template Editor
 * (useBoardColumns) and the apply flow (App.getSubitemsBoard). Cheapest first:
 *
 *   1. The item already has a subitem → its board, straight off it.
 *   2. The PARENT BOARD has a subitems (`subtasks`) column → its settings name the
 *      subitems board (`boardIds`), read it directly. This is the common case for
 *      an item with no subitems: the subitems board outlives its last subitem, so
 *      once ANY item on the board has had one, no write is needed.
 *   3. No subitems column at all — the board has never had a subitem, so its
 *      subitems board does not exist yet. Only now is a dummy "-" subitem created
 *      to make monday create it, its board read, and the dummy deleted.
 *
 * Step 3 used to run on EVERY item without subitems (only step 1 existed), and as
 * one `create_subitem { id board { columns … } }` mutation. On a brand-new board
 * monday could create the subitem yet refuse the nested `board.columns` read with
 * a 403 UserUnauthorizedException, so the delete never ran and the "-" was left on
 * the item. It is now three calls:
 *   a. create_subitem returns only the id — nothing in it can fail after the write;
 *   b. the board is read in its own query, retried while it is not readable yet;
 *   c. the dummy is deleted in a `finally`, so it goes even when (b) gives up —
 *      started but not awaited, so the caller never waits on cleanup.
 *
 * Column settings come from `settings` (JSON, API 2025-10+); see
 * columnSettingsOf in useBoardColumns.
 */
const SETTINGS_FIELDS = 'settings';
const COLUMN_FIELDS = `columns { id title type ${SETTINGS_FIELDS} }`;
const BOARD_FIELDS = `board { id ${COLUMN_FIELDS} }`;

// Waits between attempts: 4 tries over ~3.5s before a read is reported failed.
const DEFAULT_RETRY_DELAYS_MS = [500, 1000, 2000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry(fn, delaysMs) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= delaysMs.length) throw e;
      await sleep(delaysMs[attempt]);
    }
  }
}

// lib/mondayApi resolves a GraphQL error as `errors[]` and rejects on a transport
// failure — handle both, and treat a missing board as "not readable".
function boardOrThrow(res, board) {
  if (res && res.errors) {
    const err = new Error('Could not read the subitems board');
    err.errors = res.errors;
    throw err;
  }
  if (!board || !Array.isArray(board.columns)) {
    throw new Error('Subitems board is not available');
  }
  return board;
}

/** `boardIds[0]` from a subtasks column's settings (object or JSON string), or null. */
export function subitemsBoardIdFromSettings(settings) {
  if (!settings) return null;
  try {
    const s = typeof settings === 'string' ? JSON.parse(settings) : settings;
    const ids = s && s.boardIds;
    const id = Array.isArray(ids) ? ids[0] : null;
    return id === undefined || id === null ? null : id;
  } catch (e) {
    return null;
  }
}

/** The subitems board id a subtasks column points at. */
const boardIdOfColumn = (c) => (c ? subitemsBoardIdFromSettings(c.settings) : null);

async function readBoard(monday, boardId) {
  const res = await monday.api(`query { boards(ids: [${boardId}]) { id ${COLUMN_FIELDS} } }`);
  return boardOrThrow(res, res && res.data && res.data.boards && res.data.boards[0]);
}

async function readSubitemBoard(monday, subitemId) {
  const res = await monday.api(`query { items(ids:${subitemId}) { ${BOARD_FIELDS} } }`);
  return boardOrThrow(res, res && res.data && res.data.items && res.data.items[0] && res.data.items[0].board);
}

async function deleteDummy(monday, subitemId, delaysMs) {
  try {
    await withRetry(async () => {
      const res = await monday.api(`mutation { delete_item (item_id: ${subitemId}) { id } }`);
      if (res && res.errors) {
        const err = new Error('delete_item failed');
        err.errors = res.errors;
        throw err;
      }
    }, delaysMs);
  } catch (e) {
    // Never fail the caller over cleanup — the board is already in hand.
    console.error(`Could not delete the placeholder subitem ${subitemId}`, e);
  }
}

async function bootstrapWithDummy(monday, itemId, delaysMs) {
  const mk = await monday.api(
    `mutation { create_subitem (parent_item_id: ${itemId}, item_name: "-") { id } }`,
  );
  const dummyId = mk && mk.data && mk.data.create_subitem && mk.data.create_subitem.id;
  if (!dummyId) {
    const err = new Error('create_subitem returned no id');
    err.errors = mk && mk.errors;
    throw err;
  }
  try {
    return await withRetry(() => readSubitemBoard(monday, dummyId), delaysMs);
  } finally {
    // Not awaited: cleanup must always run but never delays the caller.
    // deleteDummy retries and logs on its own, and never rejects.
    deleteDummy(monday, dummyId, delaysMs);
  }
}

export async function fetchSubitemsBoard(monday, itemId, { retryDelaysMs = DEFAULT_RETRY_DELAYS_MS } = {}) {
  // One round trip covers steps 1 and 2: the item's own subitems, and the parent
  // board's subitems column (absent on a board that never had a subitem).
  const res = await monday.api(
    `query { items(ids:${itemId}) { name subitems { id ${BOARD_FIELDS} } board { columns(types: [subtasks]) { ${SETTINGS_FIELDS} } } } }`,
  );
  const item = res && res.data && res.data.items && res.data.items[0];
  if (!item) throw new Error(`Item ${itemId} could not be read`);

  // 1. an existing subitem
  const existing = (item.subitems || []).find((s) => s && s.board);
  if (existing) return { board: existing.board, itemName: item.name };

  // 2. the board's subitems column already points at the subitems board
  const subtasksColumns = (item.board && item.board.columns) || [];
  const boardId = subtasksColumns
    .map(boardIdOfColumn)
    .find((id) => id !== null);
  if (boardId !== undefined) {
    try {
      return { board: await readBoard(monday, boardId), itemName: item.name };
    } catch (e) {
      // Unexpected — the column names a board we cannot read. Fall through to the
      // dummy rather than fail: it reaches the same board through a subitem.
      console.error(`Could not read subitems board ${boardId} directly`, e);
    }
  }

  // 3. never had a subitem — make monday create the subitems board
  return { board: await bootstrapWithDummy(monday, itemId, retryDelaysMs), itemName: item.name };
}

/** The error code fetchSubitemsBoardOfBoard throws when the board has no subitems board yet. */
export const NO_SUBITEMS_COLUMN = 'NO_SUBITEMS_COLUMN';

/** Is this the "board has no subitems yet" failure (as opposed to a read that went wrong)? */
export const isNoSubitemsBoard = (error) => Boolean(error && error.code === NO_SUBITEMS_COLUMN);

/**
 * What to tell the user in that case, wherever the subitems board's columns
 * were wanted: monday creates the board (and the Subitems column) with the
 * first subitem, so one subitem anywhere on the board is the way out.
 */
export const NO_SUBITEMS_BOARD_HINT =
  'This board has no subitems. Add a subitem to any item on the board, then reopen the editor.';

/**
 * fetchSubitemsBoardOfBoard(monday, boardId) → { board }
 *
 * The board view's way to the subitems board: there is no item to hang a
 * subitem on, so only step 2 above applies — the board's Subitems column names
 * the subitems board. A board without one has no subitems board yet, and none
 * is bootstrapped from here (a board view would have to pick an item to hang
 * the dummy on): the error says so (`code: NO_SUBITEMS_COLUMN`), and the
 * editor turns it into NO_SUBITEMS_BOARD_HINT.
 */
export async function fetchSubitemsBoardOfBoard(monday, boardId) {
  const res = await monday.api(`query { boards(ids: [${boardId}]) { columns(types: [subtasks]) { ${SETTINGS_FIELDS} } } }`);
  if (res && res.errors) {
    const err = new Error('Could not read the board');
    err.errors = res.errors;
    throw err;
  }
  const board = res && res.data && res.data.boards && res.data.boards[0];
  if (!board) throw new Error(`Board ${boardId} could not be read`);
  const subId = (board.columns || []).map(boardIdOfColumn).find((id) => id !== null);
  if (subId === undefined) {
    const err = new Error('This board has no subitems yet, so no subitems board');
    err.code = NO_SUBITEMS_COLUMN;
    throw err;
  }
  return { board: await readBoard(monday, subId) };
}
